import { Input, type InputRef } from 'antd';
import { forwardRef } from 'react';
import type { TFunction } from 'i18next';
import type { Rule } from 'antd/es/form';
import { formatRubTyping, isValidRubInput, rubToKopeks } from '../lib/money';

interface AmountInputProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  allowNegative?: boolean;
  autoFocus?: boolean;
  size?: 'large' | 'middle';
  disabled?: boolean;
}

/**
 * Ruble amount input: numeric mobile keyboard (`inputMode="decimal"`), live
 * thousand-space regrouping while typing, ₽ suffix. The value is a STRING
 * ruble amount; forms convert to kopeks via lib/money at submit (SPEC §2).
 */
export const AmountInput = forwardRef<InputRef, AmountInputProps>(function AmountInput(
  { value, onChange, placeholder, allowNegative = false, autoFocus, size = 'large', disabled },
  ref,
) {
  return (
    <Input
      ref={ref}
      inputMode="decimal"
      autoComplete="off"
      suffix="₽"
      size={size}
      value={value ?? ''}
      placeholder={placeholder ?? '0'}
      autoFocus={autoFocus}
      disabled={disabled}
      onChange={(e) => onChange?.(formatRubTyping(e.target.value, allowNegative))}
    />
  );
});

/** antd Form rule: required, valid, strictly positive ruble amount. */
export function positiveAmountRule(t: TFunction): Rule {
  return {
    validator: (_rule, value: string | undefined) => {
      if (!value) return Promise.reject(new Error(t('common.amountRequired')));
      if (!isValidRubInput(value) || rubToKopeks(value) <= 0) {
        return Promise.reject(new Error(t('common.amountInvalid')));
      }
      return Promise.resolve();
    },
  };
}

/** antd Form rule: optional amount, but must parse when present (may be ±). */
export function optionalAmountRule(t: TFunction): Rule {
  return {
    validator: (_rule, value: string | undefined) => {
      if (!value || value === '-') return Promise.resolve();
      return isValidRubInput(value)
        ? Promise.resolve()
        : Promise.reject(new Error(t('common.amountInvalid')));
    },
  };
}
